// Add this method to src/services/api.js
// Add to tradeAPI object:
/*
  // Close all positions
  closeAllPositions: () => api.post('/trade/close-all'),
*/import React, { useState, useEffect, useRef } from 'react';
import { keyframes } from '@emotion/react';
import { 
  Box, 
  Flex, 
  Text, 
  Icon,
  Badge, 
  Button,
  HStack,
  useToast,
  Alert,
  AlertIcon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton
} from '@chakra-ui/react';
import { FiRefreshCw, FiBarChart2, FiZap, FiDollarSign, FiClock, FiX, FiAlertTriangle } from 'react-icons/fi';
import RateLimitError from '../components/RateLimitError';
import api from '../services/api';

// Create a rotation animation keyframe
const rotateAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/**
 * Positions List Component
 * Displays a list of trading positions with real-time updates
 */
const PositionsList = ({ 
  positions = [], 
  summary = null, 
  onRefresh = null,
  isLoading = false,
  isError = false,
  error = null 
}) => {
  const toast = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [isClosingAll, setIsClosingAll] = useState(false);
  const refreshTimerRef = useRef(null);
  
  // Confirmation modal for closing all positions
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Calculate stats
  const stats = {
    totalOpen: positions.length,
    totalLong: positions.filter(p => p.direction === 'long').length,
    totalShort: positions.filter(p => p.direction === 'short').length,
  };

  // Handle manual refresh
  const handleRefresh = async () => {
    if (isRefreshing || !onRefresh) return;
    
    setIsRefreshing(true);
    
    try {
      await onRefresh();
    } catch (err) {
    
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };
  
  // Handle closing all positions
  const handleCloseAllPositions = () => {
    if (positions.length === 0) {
      toast({
        title: "No positions to close",
        description: "You don't have any open positions to close.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Open confirmation modal
    onOpen();
  };
  
  // Execute close all positions
  const executeCloseAllPositions = async () => {
    setIsClosingAll(true);
    onClose();
    
    try {
      // Call the API endpoint to close all positions using the API service
      const response = await api.trade.closeAllPositions();
      
      if (response.data.success) {
        const successCount = response.data.data.success_count || 0;
        const failureCount = response.data.data.failure_count || 0;
        
        if (successCount > 0 && failureCount === 0) {
          toast({
            title: "Positions closed successfully",
            description: `All ${successCount} positions have been closed.`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else if (successCount > 0 && failureCount > 0) {
          toast({
            title: "Partial success",
            description: `${successCount} positions closed, ${failureCount} failed.`,
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        } else if (successCount === 0) {
          toast({
            title: "No positions closed",
            description: "Failed to close positions. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
        
        // Refresh positions
        if (onRefresh) {
          setTimeout(() => {
            onRefresh();
          }, 1000);
        }
      } else {
        throw new Error(response.data.message || "Failed to close positions");
      }
    } catch (error) {
     
      toast({
        title: "Error closing positions",
        description: error.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsClosingAll(false);
    }
  };
  
  // Set up auto-refresh timer
  useEffect(() => {
    // Function to perform auto-refresh
    const performAutoRefresh = async () => {
      if (!onRefresh) return;
      
      setIsAutoRefreshing(true);
      
      try {
        await onRefresh();
      } catch (err) {
      
      } finally {
        setTimeout(() => {
          setIsAutoRefreshing(false);
        }, 1000);
      }
    };
    
    // Set up interval for auto-refresh (15 seconds)
    refreshTimerRef.current = setInterval(performAutoRefresh, 15000);
    
    // Cleanup function
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [onRefresh]);
  
  // Loading state
  if (isLoading && positions.length === 0) {
    return (
      <Box className="flex-1 h-full">
        {/* Loading skeleton state */}
        <Box className="space-y-3">
          {[1, 2].map((i) => (
            <PositionSkeleton key={i} />
          ))}
        </Box>
      </Box>
    );
  }
  
  // Error state
  if (isError) {
    // Check for rate limit error (429)
    const isRateLimit = error?.response?.status === 429 || 
                      (error?.response?.data?.message && 
                        error.response.data.message.toLowerCase().includes('rate limit')) ||
                      error?.message?.includes('429');
    
    if (isRateLimit) {
      return (
        <RateLimitError 
          onRetry={handleRefresh} 
          retryAfter={30} // You can adjust this or fetch from response headers if available
        />
      );
    }
    
    // Regular error display for other errors
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text>{error?.message || 'Failed to load positions'}</Text>
      </Alert>
    );
  }
  
  return (
    <Box h="100%" display="flex" flexDirection="column">
      {/* Header - More compact for mobile */}
      <Flex justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
        <HStack spacing={1.5}>
          <Icon as={FiBarChart2} boxSize="4" color="green.400" />
          <Box>
            <Text fontSize={{ base: "sm", lg: "md" }} fontWeight="semibold" color="white">Open Positions</Text>
            <Text fontSize={{ base: "2xs", lg: "xs" }} color="gray.400">
              Live position tracking
            </Text>
          </Box>
        </HStack>
        
        <HStack spacing={2}>
          <Button
            size="sm"
            colorScheme="green"
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
            _hover={{ bg: "whiteAlpha.100" }}
            title="Refreshes automatically every 15 seconds"
            bg="rgba(16, 185, 129, 0.1)"
            borderColor="rgba(16, 185, 129, 0.3)"
            fontSize={{ base: "xs", md: "sm" }}
            px={{ base: 3, lg: 4 }}
            py={{ base: 1.5, lg: 2 }}
          >
            <Icon 
              as={FiRefreshCw} 
              mr={2}
              animation={(isRefreshing || isAutoRefreshing) ? `${rotateAnimation} 1s linear infinite` : 'none'}
            />
            Refresh
          </Button>
          
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            onClick={handleCloseAllPositions}
            disabled={isClosingAll || positions.length === 0}
            _hover={{ bg: "rgba(239, 68, 68, 0.1)" }}
            title="Close all open positions"
            bg="rgba(239, 68, 68, 0.05)"
            borderColor="rgba(239, 68, 68, 0.3)"
            fontSize={{ base: "xs", md: "sm" }}
            px={{ base: 3, lg: 4 }}
            py={{ base: 1.5, lg: 2 }}
            display={{ base: positions.length === 0 ? "none" : "flex", md: "flex" }}
            isLoading={isClosingAll}
            loadingText="Closing..."
          >
            <Icon 
              as={FiX} 
              mr={2}
            />
            Close All
          </Button>
        </HStack>
      </Flex>
      
   
      <Box 
        flex="1" 
        overflow="auto" 
        className="positions-container" 
        id="positionsScrollContainer"
        pr={2}
      >
        {positions.length === 0 ? (
          <EmptyPositionsState />
        ) : (
          <Box className="space-y-3" pb={4}>
            {positions.map((position) => (
              <PositionCard
                key={position.id || `${position.symbol}-${position.direction}`}
                position={position}
              />
            ))}
          </Box>
        )}
      </Box>
      
      {/* Confirmation Modal for closing all positions */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent 
          bg="rgba(15, 27, 14, 0.8)" 
          borderWidth="1px" 
          borderColor="rgba(239, 68, 68, 0.3)"
          boxShadow="0 0 20px rgba(239, 68, 68, 0.2)"
        >
          <ModalHeader display="flex" alignItems="center" color="white">
            <Icon as={FiAlertTriangle} color="red.400" mr={2} />
            Close All Positions
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody pb={6}>
            <Text color="gray.300">
              Are you sure you want to close all positions? This action will immediately close all your open positions across all symbols at the current market price.
            </Text>
            
            <Box mt={4} p={3} bg="rgba(239, 68, 68, 0.1)" borderRadius="md">
              <Text color="red.300" fontSize="sm" fontWeight="medium">
                This action cannot be undone. You may incur slippage or trading fees during execution.
              </Text>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button 
              colorScheme="gray" 
              mr={3} 
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={executeCloseAllPositions}
              isLoading={isClosingAll}
              loadingText="Closing..."
            >
              Close All Positions
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Empty state component
const EmptyPositionsState = () => (
  <Flex 
    direction="column" 
    align="center" 
    justify="center" 
    h="300px"
    border="1px dashed rgba(78, 224, 53, 0.3)" 
    borderRadius="xl"
    bg="rgba(0, 0, 0, 0.2)"
    transition="all 0.3s ease"
    className="compact-empty-state"
    _hover={{
      borderColor: "rgba(78, 224, 53, 0.5)",
      bg: "rgba(0, 0, 0, 0.3)"
    }}
  >
    <Box className="compact-empty-icon">
      <Icon as={FiBarChart2} boxSize={6} color="green.400" />
    </Box>
    <Text color="white" fontSize="lg" fontWeight="medium" mb={2} className="compact-empty-title">
      No open positions
    </Text>
    <Text color="gray.400" fontSize="sm" textAlign="center" className="compact-empty-description">
      Your open positions will appear here
    </Text>
  </Flex>
);

// Position card component - Highly optimized for mobile
const PositionCard = ({ position }) => {
  const [highlight, setHighlight] = useState(false);

  // Animation classes
  const priceClass = position.priceChanged ? 'price-updating' : '';
  const pnlClass = position.pnlChanged 
    ? (parseFloat(position.pnlPercent) >= 0 ? 'pnl-updating-positive' : 'pnl-updating-negative') 
    : '';

  // Extract position data
  const {
    symbol,
    direction,
    entryPrice,
    livePrice = entryPrice,
    takeProfit,
    stopLoss,
    pnlPercent = 0,
    leverage = 20,
    margin = 0,
    size = 0,
    openedTime,
  } = position;

  // Use the openedTime directly as it's already formatted in the API
  const timeDisplay = openedTime || 'just now';

  // Basic calculations
  const isLong = direction === 'long';
  const isProfitable = parseFloat(pnlPercent) >= 0;
  const formattedSymbol = symbol.replace(/USDT$/, '').replace(/\/USDT$/, '');
  
  // Calculate indicator position for the price tracker
  const indicatorPosition = calculateIndicatorPosition(position);
  
  // Highlight effect on updates
  useEffect(() => {
    if (position.priceChanged || position.pnlChanged) {
      setHighlight(true);
      const timer = setTimeout(() => setHighlight(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [position.priceChanged, position.pnlChanged]);

  return (
    <Box
      className={`position-compact-card ${highlight ? 'new-position' : ''}`}
      position="relative"
      mb={2}
      cursor="pointer"
      p={{ base: 2, md: 3 }}
    >
      {/* Header section */}
      <Flex justifyContent="space-between" alignItems="center" className="position-header">
        {/* Symbol and direction */}
        <Box className="position-info">
          <Flex alignItems="center" gap={2} mb={0.5} className="position-title">
            <Text fontSize={{ base: "sm", md: "md", lg: "lg" }} fontWeight="bold" color="white">
              {formattedSymbol}/USDT
            </Text>
            <Badge
              bg={isLong ? "green.500" : "red.500"}
              color="white"
              borderRadius="md"
              px={1.5}
              py={0.5}
              fontSize="2xs"
              fontWeight="semibold"
              minH="16px"
            >
              {direction.toUpperCase()}
            </Badge>
          </Flex>
          
          {/* Position meta information */}
          <Flex alignItems="center" gap={{ base: 1.5, lg: 2 }} color="gray.400" fontSize="2xs">
            <HStack spacing={0.5}>
              <Icon as={FiZap} color="yellow.400" boxSize={{ base: 2.5, lg: 3 }} />
              <Text>{leverage}x</Text>
            </HStack>
            
            <HStack spacing={0.5}>
              <Icon as={FiDollarSign} color="blue.400" boxSize={{ base: 2.5, lg: 3 }} />
              <Text>Margin: ${typeof margin === 'number' ? margin.toFixed(2) : margin}</Text>
            </HStack>
            
            <HStack spacing={0.5}>
              <Icon as={FiClock} color="purple.400" boxSize={{ base: 2.5, lg: 3 }} />
              <Text>{timeDisplay}</Text>
            </HStack>
          </Flex>
        </Box>
        
        {/* Price and PnL */}
        <Flex direction="column" alignItems="flex-end" className="text-right">
          <Text 
            fontSize={{ base: "sm", md: "md", lg: "lg" }} 
            fontWeight="bold" 
            color="white"
            className={`live-price ${priceClass}`}
          >
            ${formatPrice(livePrice, symbol)}
          </Text>
          <Text 
            fontSize={{ base: "xs", md: "sm", lg: "md" }} 
            fontWeight="bold" 
            color={isProfitable ? "#10B981" : "#EF4444"}
            className={`pnl-value ${pnlClass}`}
          >
            {isProfitable ? '+' : ''}{Math.abs(parseFloat(pnlPercent)).toFixed(2)}%
          </Text>
        </Flex>
      </Flex>
      
      {/* Position tracking line */}
      <Box className="position-track">
        {/* Entry point */}
        <Box className="position-entry" />
        
        {/* SL Marker */}
        <Box 
          className="position-marker text-red-400" 
          style={{ left: '0px' }}
        >
          SL
        </Box>
        
        {/* TP Marker */}
        <Box 
          className="position-marker text-crypto-green-400" 
          style={{ right: '0px', left: 'auto' }}
        >
          TP
        </Box>
        
        {/* Live Price Indicator */}
        <Box
          className="position-indicator"
          style={{ left: `${indicatorPosition}%` }}
        >
          {/* PnL badge */}
          <Box
            className="position-pnl-compact"
            color={isProfitable ? "green.400" : "red.400"}
          >
            {isProfitable ? '+' : ''}{Math.abs(parseFloat(pnlPercent)).toFixed(2)}%
          </Box>
          
          {/* Dot indicator */}
          <Box 
            className="position-dot" 
            borderColor={isProfitable ? "green.500" : "red.500"}
          />
        </Box>
      </Box>
      
      {/* Position labels */}
      <Box className="position-labels">
        <Box>${formatPrice(stopLoss)}</Box>
        <Box>${formatPrice(entryPrice)}</Box>
        <Box>${formatPrice(takeProfit)}</Box>
      </Box>
    </Box>
  );
};

// Loading skeleton for positions
const PositionSkeleton = () => (
  <Box
    className="position-compact-card position-skeleton"
  >
    {/* Header row */}
    <Flex justifyContent="space-between" alignItems="flex-start">
      <Box flex="1">
        <Flex alignItems="center" gap={2} mb={1}>
          <Box className="skeleton-line" w="30%" h="18px" />
          <Box className="skeleton-line" w="15%" h="16px" />
        </Flex>
        <Flex gap={2} mb={1}>
          <Box className="skeleton-line" w="15%" h="14px" />
          <Box className="skeleton-line" w="20%" h="14px" />
          <Box className="skeleton-line" w="15%" h="14px" />
        </Flex>
      </Box>
      <Box textAlign="right">
        <Box className="skeleton-line" w="80px" h="20px" mb={1} ml="auto" />
        <Box className="skeleton-line" w="60px" h="18px" ml="auto" />
      </Box>
    </Flex>
    
    {/* Track */}
    <Box className="position-track">
      <Box className="position-entry" />
      <Box className="skeleton-line" w="10px" h="10px" borderRadius="full" position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" />
    </Box>
    
    {/* Labels */}
    <Box className="position-labels">
      <Box className="skeleton-line" w="40px" h="12px" />
      <Box className="skeleton-line" w="40px" h="12px" />
      <Box className="skeleton-line" w="40px" h="12px" />
    </Box>
  </Box>
);

/**
 * Helper Functions
 */

// Format price with appropriate decimal places
const formatPrice = (price, symbol = '') => {
  if (!price || price === 0) return '0';
  
  const priceNum = parseFloat(price);
  if (isNaN(priceNum) || priceNum === 0) return '0';
  
  // Format based on price range
  if (priceNum < 0.1) return priceNum.toFixed(6);
  if (priceNum < 1) return priceNum.toFixed(5);
  if (priceNum < 10) return priceNum.toFixed(4);
  if (priceNum < 1000) return priceNum.toFixed(2);
  return priceNum.toFixed(2);
};

// Calculate position indicator location
const calculateIndicatorPosition = (position) => {
  // Temel değerleri al
  const current = parseFloat(position.livePrice) || 0;
  const entry = parseFloat(position.entryPrice) || 0;
  const sl = parseFloat(position.stopLoss) || 0;
  const tp = parseFloat(position.takeProfit) || 0;
  
  // Veri geçerliliğini kontrol et
  if (entry === 0 || sl === 0 || tp === 0) return 50;
  
  const isLong = position.direction === 'long';
  
  // Track üzerinde entry noktasının konumu (yüzde olarak)
  // SL ve TP arasındaki toplam mesafede entry'nin konumu
  const entryPosition = 50; // Entry noktasını tam ortada tutuyoruz (UI için sabit)
  
  // SHORT pozisyonlar için
  if (!isLong) {
    // Short pozisyonda, SL > Entry > TP olmalı
    
    if (current >= sl) return 0; // Fiyat SL'ye ulaştı veya geçti
    if (current <= tp) return 100; // Fiyat TP'ye ulaştı veya geçti
    
    if (current > entry) {
      // LOSS durumu: Entry ile SL arasında
      // 0 ile entryPosition (50) arasında bir değer olmalı
      // Fiyat entry'e yaklaştıkça 50'ye yaklaşır, SL'ye yaklaştıkça 0'a yaklaşır
      const ratio = (sl - current) / (sl - entry);
      return entryPosition * ratio;
    } else {
      // PROFIT durumu: Entry ile TP arasında
      // entryPosition (50) ile 100 arasında bir değer olmalı
      // Fiyat entry'e yaklaştıkça 50'ye yaklaşır, TP'ye yaklaştıkça 100'e yaklaşır
      const ratio = (entry - current) / (entry - tp);
      return entryPosition + ((100 - entryPosition) * ratio);
    }
  }
  // LONG pozisyonlar için
  else {
    // Long pozisyonda, TP > Entry > SL olmalı
    
    if (current <= sl) return 0; // Fiyat SL'ye ulaştı veya geçti
    if (current >= tp) return 100; // Fiyat TP'ye ulaştı veya geçti
    
    if (current < entry) {
      // LOSS durumu: Entry ile SL arasında
      // 0 ile entryPosition (50) arasında bir değer olmalı
      // Fiyat entry'e yaklaştıkça 50'ye yaklaşır, SL'ye yaklaştıkça 0'a yaklaşır
      const ratio = (current - sl) / (entry - sl);
      return entryPosition * ratio;
    } else {
      // PROFIT durumu: Entry ile TP arasında
      // entryPosition (50) ile 100 arasında bir değer olmalı
      // Fiyat entry'e yaklaştıkça 50'ye yaklaşır, TP'ye yaklaştıkça 100'e yaklaşır
      const ratio = (current - entry) / (tp - entry);
      return entryPosition + ((100 - entryPosition) * ratio);
    }
  }
};

export default PositionsList;